import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import axios from "axios";
import { api, getAuthHeaders } from "@/api/client";
import { useSession } from "@/services/auth0/session";
// Audience-projected wire contract: Server 993c83b, shipment-history presenter/query.
// Recorded values are display evidence only; current APIs own operational decisions.
export type HistoryValue =
  | string
  | number
  | boolean
  | null
  | HistoryValue[]
  | { [key: string]: HistoryValue };
export interface HistoryDefinition {
  availability:
    | "AVAILABLE"
    | "REDACTED"
    | "UNSUPPORTED_SCHEMA"
    | "INVALID_RECORDED_DEFINITION";
  value: { [key: string]: HistoryValue } | null;
}
export interface ShipmentHistoryRevision {
  id: string;
  shipmentId: string;
  sequence: number;
  previousRevisionId: string | null;
  predecessorAvailability: string;
  schemaVersion: number;
  kind: "CREATED" | "APPLIED_CHANGE" | "OBSERVED_BASELINE";
  recordedAt: string;
  actor: { role: string };
  cause: {
    requestId: string | null;
    reviewId: string | null;
    operationId?: string | null;
    authorizingPolicy?: string;
  };
  definition: HistoryDefinition;
  diff: {
    availability: string;
    fields:
      | { path: string; before: HistoryValue; after: HistoryValue }[]
      | null;
  };
}
export interface ShipmentHistoryPage {
  items: ShipmentHistoryRevision[];
  page: { snapshotSequence: number; nextCursor: string | null };
  current: {
    latestVisibleRevision: { id: string; sequence: number } | null;
    writeVersion: number;
    enrolled: boolean;
    updatedAt: string;
  };
}

export function historyErrorLabel(error: unknown) {
  const status = axios.isAxiosError(error) ? error.response?.status : undefined;
  return status === 401 || status === 403 || status === 404
    ? "denied"
    : status === 503
      ? "unavailable"
      : "error";
}
async function fetchHistory(
  token: string,
  shipmentId: string,
  signal: AbortSignal,
  cursor?: string,
  revisionId?: string
) {
  const { data } = await api.get<{ data: ShipmentHistoryPage }>(
    `/shipments/${encodeURIComponent(shipmentId)}/revisions${revisionId ? `/${encodeURIComponent(revisionId)}` : ""}`,
    {
      params: cursor ? { cursor } : undefined,
      headers: getAuthHeaders(token),
      signal,
    }
  );
  return data.data;
}
export function useShipmentHistory(
  shipmentId: string,
  enabled: boolean,
  updatedAt?: string
) {
  const { token, user } = useSession();
  return useInfiniteQuery({
    queryKey: ["shipmentHistory", user?.email, shipmentId, "pages", updatedAt],
    initialPageParam: emptyCursor(),
    queryFn: ({ pageParam, signal }) =>
      fetchHistory(token ?? "", shipmentId, signal, pageParam),
    getNextPageParam: (page) => page.page.nextCursor ?? undefined,
    enabled: !!token && !!user?.email && enabled,
    // Only mounted while history is open; refresh every loaded authorized page.
    refetchInterval: 30_000,
    refetchIntervalInBackground: false,
    staleTime: 30_000,
    gcTime: 0,
    retry: false,
    refetchOnWindowFocus: "always",
    refetchOnReconnect: "always",
  });
}
export function useShipmentHistoryRevision(
  shipmentId: string,
  revisionId: string | null,
  enabled: boolean
) {
  const { token, user } = useSession();
  return useQuery({
    queryKey: [
      "shipmentHistory",
      user?.email,
      shipmentId,
      "revision",
      revisionId,
    ],
    queryFn: ({ signal }) =>
      fetchHistory(
        token ?? "",
        shipmentId,
        signal,
        undefined,
        revisionId ?? undefined
      ),
    enabled: !!token && !!user?.email && !!revisionId && enabled,
    staleTime: 0,
    gcTime: 0,
    retry: false,
  });
}

function emptyCursor(): string | undefined {
  return undefined;
}
